// GENERATED from backend/services/billMemory/core — do not edit; run backend/scripts/sync-bill-memory-core.js
/**
 * Bill Memory — the lookup ladder (source spec, "The lookup ladder"; design §8, D2, D17, D21, D22).
 * Every line, scanned or typed, walks seven checks top to bottom and the first that answers
 * decides the colour: green only when exactly one item is possible, yellow (best answer on top)
 * whenever there is a choice, red only when nothing fits.
 *
 *   1 not stock (vendor)   2 this vendor's lesson   3 vendor rule   4 shop-wide lesson
 *   5 starter dictionary   6 today's matcher (exact name)   7 red
 *
 * A green then meets the safety checks that stay: unit family, new pack size, look-alike lines,
 * line maths and a not-yet-trusted quantity from memory. Price never colours a row. Pure.
 */
import { lineKeyOf, exactItem, joinedItem, candidatesFor, twinsOf } from './inventoryIndex';
import { rankChips, nameCloseness } from './ranker';
import { mathsMismatch } from './maths';
import { applyQuantityMemory, informalUnitOf } from './quantityMemory';
import { makeMatchName, joinedName, soundKey } from './matchName';

/** A lesson map's joined spellings (Fix 11 part B): joined match name → the one lesson under it, null when two share it. */
const joinedLessons = new WeakMap();
function lessonByJoined(map, joined) {
  if (!map || !joined) return null;
  let idx = joinedLessons.get(map);
  if (!idx) {
    idx = new Map();
    for (const [name, L] of map) {
      const j = joinedName(name);
      idx.set(j, idx.has(j) ? null : L);
    }
    joinedLessons.set(map, idx);
  }
  return idx.get(joined) || null;
}

const SCANNED = new Set(['scanned', 'excel', 'text']);
const MAX_CHIPS = 5; // today's form shows up to 5 suggestions (matchAgainstInventory)
const qtyLabel = (q, u) => `${+Number(q).toFixed(3)} ${u || ''}`.trim();
const TO_BASE = { kg: ['g', 1000], g: ['g', 1], l: ['ml', 1000], ltr: ['ml', 1000], ml: ['ml', 1] };
function samePack(a, b) {
  const x = TO_BASE[String(a.unit || '').toLowerCase()];
  const y = TO_BASE[String(b.unit || '').toLowerCase()];
  if (!x || !y) return String(a.unit).toLowerCase() === String(b.unit).toLowerCase() && Number(a.size) === Number(b.size);
  return x[0] === y[0] && Math.abs(Number(a.size) * x[1] - Number(b.size) * y[1]) < 1e-6;
}
const uniqEntries = (list) => {
  const seen = new Set();
  return list.filter((e) => e && !seen.has(e.item_id) && seen.add(e.item_id));
};

function decideLine(line, ctx) {
  const notes = [];
  if (line.ai_rejected) notes.push({ code: 'not_kitchen_item', params: {} });
  if (line.settled) {
    return { colour: 'green', check_no: line.source_check || 0, item_id: line.item_id == null ? null : Number(line.item_id),
      chips: [], preselect: null, reason: null, notes, qty: null, settled: true };
  }
  const inv = ctx.invIndex;
  const familyOf = ctx.familyOf || (() => null);
  // An informal unit ("3 bundles") has no fixed family: quantity memory converts it (D17).
  const fam = informalUnitOf(line.unit) ? null : familyOf(line.unit);
  const fits = (e) => !fam || !e || !e.family || e.family === fam;
  // Fix 11 part B: a joined spelling ("pesarapappu") is tried only where the normal name finds nothing —
  // the dictionary, the exact item, the lessons — and never chooses between two answers: one item and a
  // different dictionary group is no match at all.
  let k = lineKeyOf(line.printed_name, ctx.dictIndex, { joined: true });
  const exactNormal = exactItem(k.stripped, inv);
  let exactJoined = exactNormal ? null : joinedItem(k.joined, inv);
  if (k.viaJoined && exactJoined && exactJoined.key !== k.key) {
    k = lineKeyOf(line.printed_name, ctx.dictIndex);
    exactJoined = null;
  }
  const native = line.original_name ? makeMatchName(line.original_name) : null;
  const lessonIn = (map) => (map && (map.get(k.matchName) || (native ? map.get(native) : null) || lessonByJoined(map, k.joined))) || null;
  const certain = !!(ctx.vendor && ctx.vendor.certain);
  // Fix 1: a source answers only while it is On (absent = On, as before sources existed). A lesson
  // whose source is not On is simply not there: no check 1, 2 or 4 answer, and no votes.
  const sourceOn = (s) => !ctx.switches || ctx.switches[s] === undefined || ctx.switches[s] === 'on';
  const vendorLesson = certain && sourceOn('vendorLessons') ? lessonIn(ctx.lessons && ctx.lessons.vendor) : null;
  const shopLesson = sourceOn('shopLessons') ? lessonIn(ctx.lessons && ctx.lessons.shop) : null;
  const vendorCounts = certain && ctx.supply ? ctx.supply : new Map();
  const votes = {};
  for (const L of [shopLesson, vendorLesson]) {
    for (const [id, n] of Object.entries((L && L.votes) || {})) {
      const r = inv.resolveItemId(id);
      if (r) votes[r] = (votes[r] || 0) + Number(n);
    }
  }
  const words = k.words.length ? k.words : k.stripped.split(' ').filter(Boolean);
  const cands = candidatesFor(words, inv);
  const exact = exactNormal || exactJoined;
  const twins = exact ? twinsOf(exact, inv) : [];
  const cleanExact = !!(exact && !twins.length);

  const reasonFor = (lead, chip) => {
    if (!chip) return { code: 'choose', params: {} };
    if (lead === 'vendor') return { code: 'vendor_leads', params: { item: chip.name, vendor: ctx.vendor.name } };
    if (lead === 'votes') return { code: 'votes_lead', params: { item: chip.name } };
    if (lead === 'pack') return { code: 'pack_suits', params: { qty: qtyLabel(line.qty, line.unit), item: chip.name } };
    return { code: 'choose', params: {} };
  };
  const yellow = (check, list, { preselectId = null, reason = null } = {}) => {
    const r = rankChips(uniqEntries(list), {
      vendorCounts, votes, billUnit: line.unit, billQty: line.qty, familyOf,
      nameScore: (c) => nameCloseness(k.stripped, c.stripped || makeMatchName(c.name)),
    });
    let pre = r.preselect;
    let lead = r.leadReason;
    if (preselectId != null) {
      const i = r.chips.findIndex((c) => c.item_id === Number(preselectId));
      if (i > 0) r.chips.unshift(r.chips.splice(i, 1)[0]);
      pre = r.chips.length ? 0 : null;
      lead = 'given';
    }
    return { colour: 'yellow', check_no: check, item_id: null, chips: r.chips.slice(0, MAX_CHIPS), preselect: pre,
      reason: reason || reasonFor(lead, r.chips[0]), notes, qty: null };
  };
  const packLessonFor = (entry) => [vendorLesson, shopLesson]
    .find((L) => L && L.pack_size && inv.resolveItemId(L.item_id) === entry.item_id) || null;
  const unitsLessonFor = (entry) => [vendorLesson, shopLesson]
    .find((L) => L && L.units && Object.keys(L.units).length && inv.resolveItemId(L.item_id) === entry.item_id) || null;
  const green = (check, entry, reason) => {
    // Its own copy of the notes: a lower answer asked speculatively (Fix 2) never leaves notes behind.
    const own = [...notes];
    const d = { colour: 'green', check_no: check, item_id: entry.item_id, chips: [{ item_id: entry.item_id, name: entry.name, why: 'only' }],
      preselect: 0, reason, notes: own, qty: null };
    const pl = packLessonFor(entry);
    if (pl && line.pack && line.pack.size && !samePack(line.pack, { size: pl.pack_size, unit: pl.pack_unit })) {
      d.colour = 'yellow';
      d.reason = { code: 'new_pack', params: { now: qtyLabel(line.pack.size, line.pack.unit), was: qtyLabel(pl.pack_size, pl.pack_unit) } };
    }
    if (mathsMismatch(line.qty, line.unit_price, line.total_price)) {
      own.push({ code: 'maths', params: { qty: Number(line.qty), rate: Number(line.unit_price), amount: Number(line.total_price) } });
      d.colour = 'yellow';
    }
    // D17: never a piece item — the piece guard and pieceProfileVault stay the only door for those.
    const qm = familyOf(entry.unit_symbol) === 'count' ? null : applyQuantityMemory(line, unitsLessonFor(entry));
    if (qm) {
      d.qty = qm;
      own.push({ code: 'qty_memory', params: qm.note });
      // I10: a filled quantity keeps the row yellow until quantities pass their own gate (and while untrusted)
      if (!qm.trusted || !sourceOn('quantity')) d.colour = 'yellow';
    }
    return d;
  };
  // Checks 3, 5 and 6 as answers of their own: the ladder asks them in order, and a learned-once
  // lesson asks them too (Fix 2). Each returns exactly what the ladder answered before, or null.
  const vendorRuleAnswer = () => { // 3 — vendor rule (never over an exact item name: D2)
    if (!(ctx.switches && ctx.switches.vendorRule === 'on' && certain && ctx.supply && !cleanExact)) return null;
    const supplied = cands.filter((c) => (ctx.supply.get(c.item_id) || 0) > 0 && fits(c));
    if (supplied.length !== 1) return null;
    const c = supplied[0];
    // Fix 6: the one item this vendor supplies has a duplicate ("Kaju" and "Cashew") — a choice, never a
    // green: the supplied item first and pre-selected, its duplicates after it.
    const tw = twinsOf(c, inv).filter(fits);
    if (tw.length) return yellow(3, [c, ...tw], { preselectId: c.item_id, reason: { code: 'vendor_leads', params: { item: c.name, vendor: ctx.vendor.name } } });
    const pl = packLessonFor(c);
    const packNew = pl && line.pack && line.pack.size && !samePack(line.pack, { size: pl.pack_size, unit: pl.pack_unit });
    if ((ctx.supply.get(c.item_id) || 0) >= 2 && !packNew) return green(3, c, { code: 'usually_from', params: { vendor: ctx.vendor.name } });
    return yellow(3, [c, ...cands], { preselectId: c.item_id, reason: { code: 'vendor_leads', params: { item: c.name, vendor: ctx.vendor.name } } });
  };
  const dictionaryAnswer = () => { // 5 — starter dictionary
    if (!(ctx.switches && ctx.switches.dictionary === 'on' && ctx.dictIndex && k.groups.length && !cleanExact)) return null;
    const keyMatches = (inv.byKey.get(k.key) || []).filter(fits);
    if (keyMatches.length === 1 && !k.regional) return green(5, keyMatches[0], { code: 'dictionary', params: { item: keyMatches[0].name } });
    const list = uniqEntries([...keyMatches, ...cands]).filter(fits);
    return list.length ? yellow(5, list) : null;
  };
  const exactAnswer = () => { // 6 — today's matcher, an exact name only
    if (exact) {
      if (twins.length) return yellow(6, [exact, ...twins]);
      if (fits(exact)) return green(6, exact, null);
    }
    const today = line.today || {};
    if (today.status === 'exact' && today.item_id) {
      const e = inv.get(inv.resolveItemId(today.item_id));
      if (e && fits(e) && !twinsOf(e, inv).length) return green(6, e, null);
    }
    return null;
  };
  const lessonStep = (L, check, scope) => {
    if (!L || L.status === 'paused' || L.status === 'not_stock') return null;
    if (L.status === 'confusing') {
      const fromVotes = Object.keys(L.votes || {}).map((id) => inv.get(inv.resolveItemId(id)));
      const list = uniqEntries([...fromVotes, ...cands]).filter(fits);
      return list.length ? yellow(check, list) : null;
    }
    const entry = inv.get(inv.resolveItemId(L.item_id));
    if (!entry || !fits(entry)) return null;
    const entryTwins = twinsOf(entry, inv);
    if (L.status === 'learning') {
      // Fix 2 (and edge 12): a lower check that gives this same item green keeps the row green, recorded
      // as that check; one that gives another item green makes it a choice between the two.
      const lower = [check === 2 ? vendorRuleAnswer() : null, dictionaryAnswer(), exactAnswer()]
        .filter((d) => d && d.colour === 'green');
      // an item with a duplicate stays a choice (the duplicate rule is unchanged by Fix 2)
      const same = !entryTwins.length && lower.find((d) => d.item_id === entry.item_id);
      if (same) return same;
      const other = lower.find((d) => d.item_id !== entry.item_id);
      if (other) {
        const y = inv.get(other.item_id);
        return { colour: 'yellow', check_no: check, item_id: null, preselect: null, reason: { code: 'choose', params: {} }, notes, qty: null,
          chips: [{ item_id: entry.item_id, name: entry.name, why: 'lesson' }, { item_id: y.item_id, name: y.name, why: 'name' }] };
      }
      return yellow(check, [entry, ...entryTwins, ...cands], { preselectId: entry.item_id, reason: { code: 'learned_once', params: {} } });
    }
    // trusted
    const when = L.updated_at || L.created_at;
    if (entryTwins.length) {
      votes[entry.item_id] = (votes[entry.item_id] || 0) + Number(L.confirmations || 0);
      return yellow(check, [entry, ...entryTwins]);
    }
    if (scope === 'shop') {
      if (exact && exact.item_id === entry.item_id) return null; // today's exact green covers it
      const fitting = new Set(uniqEntries([entry, ...cands]).filter(fits).map((e) => e.item_id));
      if (!exact && fitting.size >= 2) { // the inventory says this name is a choice (confusing)
        votes[entry.item_id] = (votes[entry.item_id] || 0) + Number(L.confirmations || 0);
        return yellow(check, [entry, ...cands]);
      }
      return green(check, entry, { code: 'learned_shop', params: { date: when } });
    }
    return green(check, entry, { code: 'learned_vendor', params: { date: when } });
  };

  // 1 — not stock for this vendor
  if (certain && SCANNED.has(line.input_kind) && vendorLesson && vendorLesson.status === 'not_stock') {
    if ((vendorLesson.confirmations || 0) >= 2) {
      return { colour: 'left_out', check_no: 1, item_id: null, chips: [], preselect: null, reason: null, notes, qty: null };
    }
    notes.push({ code: 'removed_last_bill', params: {} });
  }
  // 2 — this vendor's lesson
  const two = lessonStep(vendorLesson, 2, 'vendor');
  if (two) return two;
  // 3 — vendor rule. Its own yellow (the vendor supplied the item on one bill only) never undoes a green
  // a lower check gives that same item — Fix 2's definition of done: no row green on one bill and
  // yellow on the next for the same name and item unless a person changed it. A lower green for
  // another item makes it a choice between the two, nothing pre-selected.
  const three = vendorRuleAnswer();
  if (three && three.colour === 'yellow' && three.preselect === 0 && three.chips.length) {
    const x = three.chips[0];
    const lower = [lessonStep(shopLesson, 4, 'shop'), dictionaryAnswer(), exactAnswer()].filter((d) => d && d.colour === 'green');
    const same = lower.find((d) => d.item_id === x.item_id);
    if (same) return same;
    const other = lower.find((d) => d.item_id !== x.item_id);
    if (other) {
      const y = inv.get(other.item_id);
      return { colour: 'yellow', check_no: 3, item_id: null, preselect: null, reason: { code: 'choose', params: {} }, notes, qty: null,
        chips: [{ item_id: x.item_id, name: x.name, why: 'vendor' }, { item_id: y.item_id, name: y.name, why: 'name' }] };
    }
  }
  if (three) return three;
  // 4 — shop-wide lesson
  const four = lessonStep(shopLesson, 4, 'shop');
  if (four) return four;
  // 5 — starter dictionary
  const five = dictionaryAnswer();
  if (five) return five;
  // 6 — today's matcher: an exact name, else today's suggestions as chips
  const six0 = exactAnswer();
  if (six0) return six0;
  const today = line.today || {};
  const fromToday = (today.suggestions || []).map((s) => (s.item_id
    ? inv.get(inv.resolveItemId(s.item_id))
    : (inv.byStripped.get(makeMatchName(s.name)) || [])[0])).filter(Boolean);
  const six = uniqEntries([...fromToday, ...cands, ...(exact ? [exact] : [])]);
  if (six.length) return yellow(6, six);
  // I15 — nothing else knows the name: items that sound like it ("kotimeera" → Kothimeera), directly or
  // through a dictionary name, are offered; a sound-alike never greens and nothing is pre-selected.
  const sk = soundKey(k.stripped);
  if (sk.length >= 4) {
    const viaDict = ctx.dictIndex && ctx.dictIndex.sounds ? [...(ctx.dictIndex.sounds.get(sk) || [])].flatMap((g) => inv.byKey.get(g) || []) : [];
    const alike = uniqEntries([...((inv.bySound && inv.bySound.get(sk)) || []), ...viaDict]).filter(fits);
    if (alike.length) {
      const r = rankChips(alike, { familyOf, billUnit: line.unit, nameScore: (c) => nameCloseness(k.stripped, c.stripped || '') });
      return { colour: 'yellow', check_no: 6, item_id: null, chips: r.chips.slice(0, MAX_CHIPS), preselect: null,
        reason: { code: 'sounds_like', params: { item: r.chips[0].name } }, notes, qty: null };
    }
  }
  // 7 — red, with anything loosely close
  const loose = [];
  for (const w of words) for (const e of candidatesFor([w], inv)) loose.push(e);
  const r = rankChips(uniqEntries(loose), { familyOf, billUnit: line.unit, nameScore: (c) => nameCloseness(k.stripped, c.stripped || '') });
  return { colour: 'red', check_no: 7, item_id: null, chips: r.chips.slice(0, MAX_CHIPS), preselect: null, reason: null, notes, qty: null };
}

/** The whole bill: each line, then lines that look alike (same name, different rate) both turn yellow. */
function decideBill(lines, ctx) {
  const ds = lines.map((l) => decideLine(l, ctx));
  const groups = new Map();
  lines.forEach((l, i) => {
    if (l.settled) return;
    const key = makeMatchName(l.printed_name);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(i);
  });
  for (const idx of groups.values()) {
    if (idx.length < 2) continue;
    const rates = new Set(idx.map((i) => lines[i].unit_price).filter((v) => v != null && v !== '').map(Number));
    if (rates.size < 2) continue;
    for (const i of idx) {
      if (ds[i].colour === 'green') ds[i].colour = 'yellow';
      ds[i].reason = { code: 'look_alike', params: {} };
    }
  }
  return ds;
}

export { decideLine, decideBill };
